// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web, étape 5) — API des écrans de MISE EN PLACE et d'administration :
//   • finance : plan comptable de DÉMONSTRATION, exercices (ouverture, clôture en deux temps), écriture manuelle
//     (OD d'inventaire), comptes de trésorerie (README finance § 5) ;
//   • aides : types d'aide d'un dispositif (préparer, valider par une autre personne, désactiver — README aides) ;
//   • gouvernance : déports (déclarer, terminer — README socle § 3) ; liste tolérante (route proposée DM-WEB-005) ;
//   • paie : arrêt du lot en deux temps (demander, confirmer ou refuser par une autre personne — README paie § 4).
// Réponses { success, data } ; tout POST financier porte l'en-tête Idempotency-Key (clé créée à l'ouverture de la
// fenêtre). Le serveur reste seul juge : toute erreur est rendue telle qu'il l'envoie.
// ═══════════════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { routeAbsente } from './apiSocial';

const FIN = '/api/dispositifs/finance';
const AIDES = '/api/dispositifs/aides';
const GOUV = '/api/dispositifs/gouvernance';
const PAIE = '/api/dispositifs/paie';

function client(token) {
  const entetes = token ? { Authorization: `Bearer ${token}` } : {};
  const get = async (url, params) => (await axios.get(url, { params, headers: entetes })).data.data;
  const post = async (url, corps, cle) => (await axios.post(url, corps || {}, { headers: cle ? { ...entetes, 'Idempotency-Key': cle } : entetes })).data.data;
  const sansVide = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined));
  /** Première route présente parmi deux (route proposée, puis variante). */
  const auChoix = async (a, b) => { try { return await a(); } catch (e) { if (routeAbsente(e)) return b(); throw e; } };
  return {
    // Finance : mise en place (DÉMONSTRATION)
    installerPlanDemo: (org, cle) => post(`${FIN}/organisations/${org}/plan-demo`, {}, cle),
    ouvrirExercice: (org, annee, cle) => post(`${FIN}/organisations/${org}/exercices`, { annee }, cle),
    cloturerExercice: (org, annee, etape, cle) => post(`${FIN}/organisations/${org}/exercices/${annee}/cloture`, { etape }, cle),
    saisirEcriture: (org, corps, cle) => post(`${FIN}/organisations/${org}/ecritures`, corps, cle),
    validerEcriture: (id, pieceId, cle) => post(`${FIN}/ecritures/${id}/valider`, pieceId ? { piece_id: pieceId } : {}, cle),
    creerCompteTresorerie: (org, corps, cle) => post(`${FIN}/organisations/${org}/comptes-tresorerie`, corps, cle),
    // Aides : types d'aide (la liste est une donnée décidée par le bureau ; les montants viennent du Centre de configuration)
    typesAide: (dispositifId) => get(`${AIDES}/dispositifs/${dispositifId}/types`),
    preparerTypeAide: (dispositifId, corps) => post(`${AIDES}/dispositifs/${dispositifId}/types`, corps),
    validerTypeAide: (id) => post(`${AIDES}/types/${id}/valider`),
    desactiverTypeAide: (id, motif) => post(`${AIDES}/types/${id}/desactiver`, { motif }),
    // Gouvernance : déports
    declarerDeport: (corps) => post(`${GOUV}/deports`, corps),
    terminerDeport: (id, dateFin) => post(`${GOUV}/deports/${id}/terminer`, sansVide({ date_fin: dateFin })),
    /** Liste des déports d'une organisation (route proposée DM-WEB-005 ; absente → « Fonction bientôt disponible »). */
    deports: (org, { page = 1, taille = 25 } = {}) => auChoix(
      () => get(`${GOUV}/organisations/${org}/deports`, { page, taille }),
      () => get(`${GOUV}/deports`, { organisation_id: org, page, taille }),
    ),
    chercherPersonnesGouv: (q) => get(`${GOUV}/personnes`, { q }),
    // Paie (mode vérification) : arrêt du lot en deux temps, sans aucune transmission
    demanderArretLot: (periode, org, cle) => post(`${PAIE}/periodes/${periode}/arret/demander`, { organisation_id: org }, cle),
    confirmerArretLot: (periode, org, cle) => post(`${PAIE}/periodes/${periode}/arret/confirmer`, { organisation_id: org }, cle),
    refuserArretLot: (periode, org, motif, cle) => post(`${PAIE}/periodes/${periode}/arret/refuser`, { organisation_id: org, motif }, cle),
  };
}

/** Client des écrans de mise en place pour le composant courant. */
export function useMiseEnPlace() {
  const { token } = useAuth();
  return useMemo(() => client(token), [token]);
}

// ─── Libellés ──────────────────────────────────────────────────────────────────────────────────
export const ETATS_EXERCICE = { OUVERT: ['info', 'Ouvert'], CLOTURE_DEMANDEE: ['alerte', 'Clôture demandée'], CLOTURE: ['neutre', 'Clos'] };
export const FORMES_AIDE = { FORFAIT: 'Forfait', BAREME: 'Barème (paliers)', CAS_PAR_CAS: 'Cas par cas (plafonné)' };
export const AYANTS_DROIT = { AUCUN: 'Le salarié seulement', CONJOINT: 'Conjoint', ENFANT: 'Enfant', PARENT: 'Parent', TOUS: 'Tout ayant droit' };
export const PIECES_AIDE = { JUSTIFICATIF: 'Justificatif', FACTURE: 'Facture', DECISION: 'Décision', AUTRE: 'Autre' };
export const ETATS_TYPE_AIDE = { A_VALIDER: ['alerte', 'À valider'], ACTIF: ['succes', 'Actif'], DESACTIVE: ['neutre', 'Désactivé'] };
export const CIRCUITS_AIDE = { PRESIDENT: 'Décision du Président', COMMISSION: 'Décision de la commission sociale' };
export const LIENS_DEPORT = { CONJOINT: 'Conjoint', ENFANT: 'Enfant', PARENT: 'Parent', FRERE_SOEUR: 'Frère ou sœur', ALLIE: 'Allié (belle-famille)', AUTRE: 'Autre lien' };
